import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import format from 'string-template';

import {
  Card,
  Container,
  Description,
  HeaderContainer,
  Title,
  TitleContainer,
} from '../../../../../../components/v2/Card';
import InputLabel from '../../../../../../components/v2/InputLabel';
import Select from '../../../../../../components/Select';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import useToast from '../../../../../hooks/useToast';
import UserActivityProfile, { WeightsExperienceText } from '../../../../../Model/UserActivityProfile';
import MultiSelectFilter from '../../../../MultiSelectFilter';
import { StyledLoadingPage } from '../NutritionSettings/styles';

import {
  DAY_OPTIONS,
  ENVIRONMENT_OPTIONS,
  MIN_WORKOUT_DAYS,
  MAX_WORKOUT_DAYS,
} from './constants';
import texts from './texts.json';

const WorkoutPreferences = ({ clientId }) => {
  const [activityProfile, setActivityProfile] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [forceResetCounter, setForceResetCounter] = useState(0);
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { userId } = useSessionStore();
  // MultiSelectFilter fires onValuesSelected on mount with the initial values — skip that call
  const isDaysInitializedRef = useRef(false);

  useEffect(() => {
    setIsReady(false);
    isDaysInitializedRef.current = false;

    const init = async () => {
      const doc = new UserActivityProfile(clientId);
      await doc.init();
      if (isComponentMountedRef.current) {
        setActivityProfile(doc);
        setIsReady(true);
      }
    };

    init();
  }, [clientId, isComponentMountedRef]);

  const handleDaysChange = useCallback(
    async (values) => {
      if (!isDaysInitializedRef.current) {
        isDaysInitializedRef.current = true;
        return;
      }
      const hasNoSavedDays = activityProfile.preferredWorkoutDays.length === 0;
      const effectiveMin = hasNoSavedDays ? 1 : MIN_WORKOUT_DAYS;
      if (values.length < effectiveMin || values.length > MAX_WORKOUT_DAYS) {
        showToast(format(texts.daysOutOfRange, { min: MIN_WORKOUT_DAYS, max: MAX_WORKOUT_DAYS }), { error: true });
        isDaysInitializedRef.current = false;
        setForceResetCounter((c) => c + 1);
        return;
      }
      try {
        await activityProfile.updatePreferences({ preferredWorkoutDays: values }, userId);
      } catch (error) {
        showToast(format(texts.fail, { error }), { error: true });
      }
    },
    [activityProfile, userId, showToast],
  );

  const handleWorkoutLocationChange = useCallback(
    async (event) => {
      try {
        await activityProfile.updatePreferences({ workoutLocation: event.target.value }, userId);
      } catch (error) {
        showToast(format(texts.fail, { error }), { error: true });
      }
    },
    [activityProfile, userId, showToast],
  );

  if (!isReady) {
    return <StyledLoadingPage />;
  }

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.title}</Title>
        </TitleContainer>
      </HeaderContainer>
      <Container>
        <InputLabel>{texts.preferredDays}</InputLabel>
        <MultiSelectFilter
          key={forceResetCounter}
          options={DAY_OPTIONS}
          initialValues={activityProfile.preferredWorkoutDays}
          onValuesSelected={handleDaysChange}
          filterIcon={null}
          hideAllOption
        />
      </Container>
      <Container>
        <Select
          label={texts.workoutLocation}
          options={ENVIRONMENT_OPTIONS}
          value={activityProfile.workoutLocation ?? ''}
          onChange={handleWorkoutLocationChange}
          displayEmpty
        />
      </Container>
      <Container>
        <InputLabel>{texts.experienceLevel}</InputLabel>
        <Description>
          {WeightsExperienceText[activityProfile.weightsExperience] ?? texts.experienceLevelNotSet}
        </Description>
      </Container>
    </Card>
  );
};

WorkoutPreferences.propTypes = {
  clientId: PropTypes.string.isRequired,
};

export default compose(observer)(WorkoutPreferences);
